import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import T, {Translate} from '../../i18n/i18n';
import { Window, Header, HeaderTitle, Background, ButtonBar, Button, UploadButton, ButtonSpace, SheetWrap, HeaderReset, Resize, UploadInput } from './CharSheet.styles';
import { DetailWindow, DetailBackground, DetailLabel, DetailInput, DetailTextarea, DetailButton } from './DetailWindow.styles';
import { charactersActions, configActions } from '../../../redux/actions';
import { saveSheet, getSheetAsPromise } from '../../../services/chars';
import { download } from '../../../services/importExport';
import Keyboard from '../../../services/keyboardShortcuts';

import PageManager from '../../TemplateBuilder/SheetBuilder/PageManager';
import { Paper, PaperRatio, PaperContent, ContentRender } from '../../TemplateBuilder/SheetBuilder/SheetBuilder.styles';

export default ({tableId}) => {

    const dispatch = useDispatch();
    const opened = useSelector(state => state.charactersState.sheetOpened);
    const selectedSheet = useSelector(state => state.charactersState.sheettemplate);
    const selectedCharId = useSelector(state => state.charactersState.sheetCharId);
    const characters = useSelector(state => state.charactersState.characters);
    const shortcuts = useSelector(state => state.charactersState.sheetShortcuts);
    const openedDetail = useSelector(state => state.charactersState.sheetOpenedDetail);
    const detailId = useSelector(state => state.charactersState.sheetOpenedDetailId);

    const [hasShortcut, setHasShortcut] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [detailData, setDetailData] = useState({});
    const [overlayed, setOverlayed] = useState(false);
    const [pages, setPages] = useState(1);

    const rows = 52;
    const cols = 32;
    const pageManager = useRef(null);
    const renderRef = useRef([]);
    const charactersRef = useRef(null);
    const windowRef = useRef(null);
    const resizeRef = useRef(null);

    const close = useCallback(() => {
        setOverlayed(false);

        if (windowRef.current) {
            windowRef.current.parentNode.style = null;
        }
        dispatch(charactersActions.setSheetFormOpened(false));
    }, [dispatch]);

    const reset = useCallback(() => {
        setOverlayed(false);

        if (windowRef.current) {
            windowRef.current.parentNode.style = null;
        }
    }, []);

    const closeDetail = useCallback(() => {
        dispatch(charactersActions.setOpenedDetail(false, ''));
    }, [dispatch]);

    useEffect(() => {
        charactersRef.current = characters;
    }, [characters]);

    const handleRemoveShortcut = useCallback(() => {
        if(!overlayed) {
            close();
        }
        dispatch(charactersActions.removeSheetShortcut(selectedCharId));
    }, [dispatch, close, overlayed, selectedCharId]);

    const handleAddShortcut = useCallback((keepOpened = false) => {
        let selectedChar = {};
        for(let char of charactersRef.current) {
            if (char.id === selectedCharId) {
                selectedChar = char;
                break;
            }
        }

        dispatch(charactersActions.addSheetShortcut({
            id: selectedChar.id,
            name: selectedChar.name,
            avatar: selectedChar.avatar,
        }));

        if (!keepOpened) {
            close();
        }
    }, [selectedCharId, dispatch, close]);

    useEffect(() => {
        let escapeKey = () => {
            if (openedDetail) {
                dispatch(charactersActions.setOpenedDetail(false, ''));
                return false;
            }

            if (opened) {
                setOverlayed(false);
                dispatch(charactersActions.setSheetFormOpened(false));
                return false;
            }

            return true;
        };

        Keyboard.register({key:'ESCAPE'}, escapeKey);

        return () => {
            Keyboard.unregister({key:'ESCAPE'}, escapeKey);
        }
    }, [openedDetail, opened, dispatch]);

    const setDetailValue = (key, value) => {
        setDetailData(pageManager.current.setDetailValue(key, value));
    };

    const handleFileLoading = (file) => {
        try {
            let reader = new FileReader();
            reader.onload = function(evt) {
                pageManager.current.setValues(JSON.parse(evt.target.result));
                pageManager.current.buildSheet();
            };
            reader.readAsText(file[0]);
            document.querySelector('#file-import').value = '';
            setIsExporting(false);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        pageManager.current = new PageManager();
        pageManager.current.setRows(rows);
        pageManager.current.setCols(cols);
        pageManager.current.setRenderTarget(renderRef.current);
        pageManager.current.setPageChangeCallback(p => setPages(p));
        
        const resizeFn = () => {pageManager.current.resize();}
        resizeFn();

        window.addEventListener('resize', resizeFn);

        let windowPos = [0,0];
        let clickPos = [0,0];
        let windowSize = [0,0];
        let moving = false;
        let resizing = false;

        const onMouseDownTitle = (e) => {
            let size = [windowRef.current.parentNode.offsetWidth/2, windowRef.current.parentNode.offsetHeight/2];
            windowPos = [e.offsetX - size[0], e.offsetY - size[1]];
            moving = true;
            setOverlayed(state => !state ? true : state);
        };

        const onMouseDownResize = (e) => {
            console.log(e);
            windowSize = [windowRef.current.parentNode.offsetWidth, windowRef.current.parentNode.offsetHeight];
            clickPos = [e.pageX, e.pageY];
            resizing = true;
            setOverlayed(state => !state ? true : state);
        };

        const onMouseMove = (e) => {
            if (moving) {
                windowRef.current.parentNode.style.left = (e.pageX - windowPos[0]) + 'px';
                windowRef.current.parentNode.style.top = (e.pageY - windowPos[1]) + 'px';
            }
            if (resizing) {
                windowRef.current.parentNode.style.width = (windowSize[0] + 2*(e.clientX - clickPos[0])) + 'px';
                windowRef.current.parentNode.style.height = (windowSize[1] + 2*(e.clientY - clickPos[1])) + 'px';
                windowRef.current.parentNode.style.maxHeight = windowRef.current.parentNode.style.height;
            }
        };
        const onMouseUp = (e) => {
            moving = false;
            if (resizing) {
                resizing = false;
                resizeFn();
            }
        };

        windowRef.current.addEventListener('mousedown', onMouseDownTitle);
        resizeRef.current.addEventListener('mousedown', onMouseDownResize);
        document.addEventListener('mouseup', onMouseUp);
        document.addEventListener('mousemove', onMouseMove);

        let wRef = windowRef.current;
        let rRef = resizeRef.current;

        return () => {
            window.removeEventListener('resize', resizeFn);

            wRef.removeEventListener('mouseDown', onMouseDownTitle);
            rRef.addEventListener('mousedown', onMouseDownResize);
            document.removeEventListener('mouseUp', onMouseUp);
            document.removeEventListener('mouseMove', onMouseMove);
        }
    }, []);

    useEffect(() => {
        setHasShortcut(shortcuts.some(s => s.id === selectedCharId));
    }, [shortcuts, selectedCharId]);

    useEffect(() => {
        if (pageManager.current) {
            pageManager.current.setDetailState(openedDetail, detailId);
            setDetailData(pageManager.current.getDetailData());
        }
    }, [detailId, openedDetail]);

    useEffect(() => {
        if (opened) {

            pageManager.current.resize();

            getSheetAsPromise(tableId, selectedCharId, selectedSheet.id).then((res) => {
                let char = charactersRef.current && charactersRef.current.length ? charactersRef.current.find(i => i.id === selectedCharId) : null;
                let values = res.data.payload;
                pageManager.current.clearValues();
                if (typeof values === 'string') {
                    values = JSON.parse(values);
                    if (char) {
                        values['token'] = char.id;
                    }
                    pageManager.current.setValues(values);
                } else {
                    pageManager.current.setValues({});
                }
                pageManager.current.buildSheet(true);
            }).catch((e) => {
                console.error(Object.assign(e,{component:'Table/CharSheet', id: 'ParseValueGetSheet'}));
            });

            pageManager.current.setFieldValueChange(async (sheet) => {
                await saveSheet(tableId, selectedCharId, selectedSheet.id, sheet);
                dispatch(charactersActions.setSheetValue(sheet));
            });

            pageManager.current.setAfterMacroRoll(() => {
                closeDetail();
                handleAddShortcut(overlayed);
                dispatch(configActions.jumpToTab(0));
            });
        }
    }, [opened, overlayed, selectedCharId, selectedSheet, closeDetail, handleAddShortcut, dispatch, tableId]);

    useEffect(() => {
        if (pageManager.current) {
            pageManager.current.reimport();
        }
    }, [pages]);
    
    useEffect(() => {
        if (selectedSheet) {
            try {
                let content = JSON.parse(selectedSheet.content);
                pageManager.current.import(content);
            } catch (e) {
                console.error(Object.assign(e,{component:'Table/CharSheet', id: 'selectedSheet.content invalido'}));
            }
        }
    }, [selectedSheet]);

    const handleExportFile = () => {
        if (!pageManager || !pageManager.current) {
            return;
        }

        let fileName = (new Date()).getTime();
        for(let char of charactersRef.current) {
            if (char.id === selectedCharId) {
                fileName = char.name || fileName;
                break;
            }
        }

        download(JSON.stringify(pageManager.current.getValues()), fileName + '.rpgsheet', 'text/plain');
        setIsExporting(false);
    }

    const keyCancel = (e) => {
        e.stopPropagation();
    }

    return <>
        <Background onKeyDown={keyCancel} className={opened && !overlayed ? 'opened' : ''} onClick={(e) => {close()}} />
        <Window onKeyDown={keyCancel} className={opened ? 'opened' : ''}>
            <Header ref={windowRef}>
                <HeaderTitle><T value="charsheet.title"/></HeaderTitle>
                <HeaderReset onClick={()=>reset()} title={Translate('charsheet.reset')}/>
            </Header>
            <SheetWrap className="scrollbar">
                {Array(pages).fill(0).map((page,i) => <Paper className="charsheet" key={i}>
                    <PaperRatio>
                        <PaperContent className="no-border">
                            <ContentRender ref={el => renderRef.current[i] = el} cols={cols} rows={rows} className="content-render" />
                        </PaperContent>
                    </PaperRatio>
                </Paper>)}
            </SheetWrap>
            <ButtonBar>
                <UploadInput accept=".rpgsheet" onChange={(e) => {handleFileLoading(e.target.files)}} name="file-import" id="file-import" />
                {isExporting ? <>
                    <UploadButton color={"#fa6"} htmlFor="file-import"><T value="charsheet.import" /></UploadButton>
                    <Button color={"#9bf"} onClick={()=>handleExportFile()}><T value="charsheet.export" /></Button>
                    <Button color={"#999"} onClick={()=>setIsExporting(false)}><T value="cancel" /></Button>
                </> : <>
                    {!hasShortcut && <Button color={"#69f"} onClick={()=>handleAddShortcut()}><T value="charsheet.addShortcut" /></Button>}
                    {hasShortcut && <Button color={"#f96"} onClick={()=>handleRemoveShortcut()}><T value="charsheet.removeShortcut" /></Button>}
                    <Button color={"#999"} onClick={()=>setIsExporting(true)}><T value="charsheet.importexport" /></Button>
                </>}
                <ButtonSpace/>
                <Button color={"#999"} onClick={close}><T value="close" /></Button>
                <Resize ref={resizeRef}/>
            </ButtonBar>
        </Window>
        <DetailBackground onKeyDown={keyCancel} className={openedDetail ? 'opened' : ''} onClick={(e) => {closeDetail()}} />
        <DetailWindow onKeyDown={keyCancel} className={openedDetail ? 'opened' : ''}>
            <DetailLabel>
                <T value={"templatebuilder.valueDetail"} />
                <DetailButton color={'#f53'} icon={'\\F0156'} title={Translate('close')} onClick={closeDetail}></DetailButton>
            </DetailLabel>
            <DetailInput value={detailData['value'] || ''} onChange={(e) => {setDetailValue('value', e.target.value)}} onBlur={() => {pageManager.current.persistDetail()}}/>
            <DetailLabel>
                <T value={"templatebuilder.descDetail"} />
                <DetailButton color={'#0c9'} icon={'\\F017A'} title={Translate('templatebuilder.commentDetail')} onClick={() => {pageManager.current.sendDetailToChat()}}></DetailButton>
            </DetailLabel>
            <DetailTextarea className={'scrollbar'} value={detailData['description'] || ''} onChange={(e) => {setDetailValue('description', e.target.value)}}  onBlur={() => {pageManager.current.persistDetail()}}/>
            <DetailLabel>
                <T value={"templatebuilder.macroDetail"} />
                <DetailButton color={'#09f'} icon={'\\F076E'} title={Translate('templatebuilder.rollDetail')} onClick={() => {pageManager.current.rollDetail()}}></DetailButton>
            </DetailLabel>
            <DetailTextarea className={'scrollbar'} value={detailData['macro'] || ''} onChange={(e) => {setDetailValue('macro', e.target.value)}} onBlur={() => {pageManager.current.persistDetail()}}/>
        </DetailWindow>
    </>;

};